<template>
  <div class="flex flex-col gap-4 text-left" data-testid="pin-step-3">
    <div class="h-48 w-full overflow-hidden rounded-xl border border-gray-100">
      <GoogleMaps
        v-if="point"
        :latitude="point.lat"
        :longitude="point.lng"
        :radius="geo.reachRadius.value != null ? Number(geo.reachRadius.value) : undefined"
        :address="geo.formattedAddress.value"
        :draggable="false"
        :map-type="mapType"
        :show-info="false"
      />
    </div>

    <dl class="divide-y divide-gray-100 rounded-xl border border-gray-100 bg-white text-sm">
      <div class="flex flex-wrap gap-2 px-4 py-2.5" data-testid="pin-summary-unit">
        <dt class="w-40 shrink-0 text-gray-500">Unidade</dt>
        <dd class="flex-1 text-gray-800">
          {{ unitName || '—' }} · {{ unitTypeLabel(geo.unitType.value) }}
        </dd>
      </div>

      <div class="flex flex-wrap gap-2 px-4 py-2.5" data-testid="pin-summary-origin">
        <dt class="w-40 shrink-0 text-gray-500">Origem do ponto</dt>
        <dd class="flex-1 text-gray-800">{{ originLabel }}</dd>
      </div>

      <div class="flex flex-wrap gap-2 px-4 py-2.5" data-testid="pin-summary-coordinates">
        <dt class="w-40 shrink-0 text-gray-500">Coordenadas</dt>
        <dd class="flex-1 font-mono text-gray-800">{{ coordinates }}</dd>
      </div>

      <div
        v-if="(geo.movedDistanceM.value ?? 0) > 0"
        class="flex flex-wrap gap-2 px-4 py-2.5"
        data-testid="pin-summary-moved"
      >
        <dt class="w-40 shrink-0 text-gray-500">Pino movido</dt>
        <dd class="flex-1 text-gray-800">
          {{ formatMeters(geo.movedDistanceM.value) }} para {{ geo.movedBearing.value }}
        </dd>
      </div>

      <div class="flex flex-wrap gap-2 px-4 py-2.5" data-testid="pin-summary-radius">
        <dt class="w-40 shrink-0 text-gray-500">Raio</dt>
        <dd class="flex-1 text-gray-800">
          {{ geo.reachRadius.value }} m
          <span v-if="geo.originalRadius.value != null" class="text-gray-500">
            (antes: {{ geo.originalRadius.value.toLocaleString('pt-BR') }} m)
          </span>
        </dd>
      </div>

      <div
        v-if="doctorSummary"
        class="flex flex-wrap gap-2 px-4 py-2.5"
        data-testid="pin-summary-checks"
      >
        <dt class="w-40 shrink-0 text-gray-500">Checks afetados (14 dias)</dt>
        <dd class="flex-1 text-gray-800">
          {{ doctorSummary.total - doctorSummary.rejected }} aceitos ·
          {{ doctorSummary.rejected }} rejeitados · {{ doctorSummary.doctors }}
          {{ doctorSummary.doctors === 1 ? 'médico' : 'médicos' }}
        </dd>
      </div>
    </dl>

    <v-checkbox
      class="-ml-1"
      color="primary"
      density="comfortable"
      hide-details
      :model-value="confirmedOnBuilding"
      @update:model-value="confirmedOnBuilding = !!$event"
      data-testid="pin-confirm-checkbox"
    >
      <template #label>
        <span class="text-sm text-gray-700">
          Confirmo pela vista de satélite que o marcador está sobre o prédio onde os médicos
          realizam o check.
        </span>
      </template>
    </v-checkbox>

    <template v-if="geo.needsReason.value">
      <div class="w-full sm:max-w-[360px]">
        <v-select
          label="* Motivo da alteração"
          variant="outlined"
          density="comfortable"
          hide-details="auto"
          :items="reasonOptions"
          :model-value="reason"
          @update:model-value="reason = $event"
          data-testid="pin-reason-select"
        />
      </div>

      <GenericInput
        :label="isOtherReason ? '* Detalhe' : 'Detalhe (opcional)'"
        :model-value="reasonDetail"
        @update:model-value="reasonDetail = $event ?? ''"
        width="100%"
        data-testid="pin-reason-detail-input"
      />
    </template>

    <GenericInput
      v-if="geo.isRadiusOffPreset.value"
      label="* Justificativa do raio fora do preset"
      :model-value="radiusJustification"
      @update:model-value="radiusJustification = $event ?? ''"
      width="100%"
      :hint="`Preset do tipo: ${geo.radiusPreset.value} m`"
      persistent-hint
      data-testid="pin-radius-justification-input"
    />

    <div class="rounded-xl bg-[#EEF1F3] p-3 text-xs text-gray-600">
      <p>
        Fica registrado: quem alterou, de onde para onde, raio antes e depois, motivo e origem do
        ponto.
      </p>
      <p class="mt-1">A alteração vale para os próximos check-ins; os anteriores não mudam.</p>
      <p v-if="mode === 'form'" class="mt-1">
        O pino é gravado junto com o cadastro da unidade, ao salvar o formulário.
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import GoogleMaps from '@/components/GoogleMaps.vue'
import GenericInput from '@/components/designSystem/Inputs/GenericInput.vue'
import {
  PIN_CHANGE_REASON_LABELS,
  type PinChangeReason
} from '@/Network/Types/Responses/PinSuggestionResponses'
import { formatMeters } from '@/utils/geo'
import { unitTypeLabel } from '../../unitType'
import type { UnitGeolocationApi } from '../../composables/useUnitGeolocation'
import type { PinModalMode } from './pinModalTypes'

type DoctorSummary = { total: number; rejected: number; doctors: number; days: number } | null

const props = defineProps<{
  geo: UnitGeolocationApi
  unitName: string
  mapType: 'roadmap' | 'satellite'
  doctorSummary: DoctorSummary
  mode: PinModalMode
}>()

/**
 * Os campos editáveis do passo 3 vivem no estado compartilhado do composable.
 * Desestruturar os refs deixa o template escrever neles sem mutar a prop `geo`.
 */
const { confirmedOnBuilding, reason, reasonDetail, radiusJustification } = props.geo

const reasonOptions = (Object.keys(PIN_CHANGE_REASON_LABELS) as PinChangeReason[]).map((value) => ({
  title: PIN_CHANGE_REASON_LABELS[value],
  value
}))

const point = computed(() => props.geo.currentPoint.value)

const coordinates = computed(() =>
  point.value ? `${point.value.lat.toFixed(6)}, ${point.value.lng.toFixed(6)}` : '—'
)

const isOtherReason = computed(() => props.geo.reason.value === 'OTHER')

/** Traduz a origem para o que o usuário fez, não para o enum do backend. */
const originLabel = computed(() => {
  const confidence =
    props.geo.confidence.value === 'BUILDING' ? 'Prédio identificado' : 'Endereço aproximado'

  switch (props.geo.origin.value) {
    case 'PLACES':
      return `Google Places · ${confidence}`
    case 'ADDRESS':
      return 'Endereço do cadastro'
    case 'LINK':
      return 'Link do Google Maps'
    case 'SUGGESTION':
      return 'Sugestão dos checks'
    case 'MANUAL':
      return 'Ajuste manual no mapa'
    default:
      return '—'
  }
})
</script>
